import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import ProductCard from "@/components/ProductCard";
import { api } from "@/lib/api";
import { Filter } from "lucide-react";

export default function Discover() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "latest";
  const ptype = params.get("type") || "";
  const minPrice = params.get("min_price") || "";
  const maxPrice = params.get("max_price") || "";

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", {
        params: {
          q: q || undefined,
          category: category || undefined,
          sort,
          product_type: ptype || undefined,
          min_price: minPrice || undefined,
          max_price: maxPrice || undefined,
          limit: 48,
        },
      })
      .then(({ data }) => {
        setItems(data.items);
        setTotal(data.total);
      })
      .finally(() => setLoading(false));
  }, [q, category, sort, ptype, minPrice, maxPrice]);

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  return (
    <Layout>
      <section className="border-b border-[#E5E3DD] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
          <div className="overline mb-2">Discover</div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight">
            {q ? <>Results for "<em className="not-italic text-[#702A32]">{q}</em>"</> : "Browse the marketplace"}
          </h1>
          <p className="text-[#5C5A56] font-light mt-3">{total} products across {cats.length} categories</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid md:grid-cols-12 gap-8">
        <aside className="md:col-span-3" data-testid="discover-filters">
          <div className="overline mb-4 flex items-center gap-2"><Filter className="w-4 h-4" strokeWidth={1.5} /> Filters</div>

          <FilterGroup label="Category">
            <button onClick={() => update("category", "")} className={`block text-left text-sm py-1 ${!category ? "text-[#702A32]" : "text-[#5C5A56] hover:text-[#1A1A18]"}`}>All</button>
            {cats.map((c) => (
              <button key={c.category_id} onClick={() => update("category", c.category_id)} className={`block text-left text-sm py-1 ${category === c.category_id ? "text-[#702A32]" : "text-[#5C5A56] hover:text-[#1A1A18]"}`} data-testid={`filter-cat-${c.slug}`}>
                {c.name}
              </button>
            ))}
          </FilterGroup>

          <FilterGroup label="Product type">
            {[["", "All"], ["STANDARD", "Standard"], ["POD", "Print-on-Demand"], ["CUSTOM", "Customizable"]].map(([v, l]) => (
              <button key={v} onClick={() => update("type", v)} className={`block text-left text-sm py-1 ${ptype === v ? "text-[#702A32]" : "text-[#5C5A56] hover:text-[#1A1A18]"}`} data-testid={`filter-type-${v || "all"}`}>
                {l}
              </button>
            ))}
          </FilterGroup>

          <FilterGroup label="Price">
            <div className="flex gap-2">
              <input className="input-field" placeholder="Min" value={minPrice} onChange={(e) => update("min_price", e.target.value)} data-testid="filter-min-price" />
              <input className="input-field" placeholder="Max" value={maxPrice} onChange={(e) => update("max_price", e.target.value)} data-testid="filter-max-price" />
            </div>
          </FilterGroup>
        </aside>

        <div className="md:col-span-9">
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm text-[#5C5A56]">{items.length} shown</span>
            <select value={sort} onChange={(e) => update("sort", e.target.value)} className="input-field max-w-xs" data-testid="sort-select">
              <option value="latest">Latest</option>
              <option value="trending">Trending</option>
              <option value="top-rated">Top rated</option>
              <option value="price-asc">Price · low to high</option>
              <option value="price-desc">Price · high to low</option>
            </select>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-[#EFECE6] animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-testid="discover-grid">
              {items.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="mb-8">
      <div className="font-medium text-sm uppercase tracking-wide text-[#1A1A18] mb-3">{label}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}
